/* NewUserPage auto-generated by npm run generator */
import { Button, TextField, Typography } from "@material-ui/core"
import clsx from "clsx"
import { usePasswordChecker } from "components/atoms/PasswordChecker"
import PortalForm, { PortalSubmitButton } from "components/molecules/PortalForm"
import * as firebase from "firebase/app"
import "firebase/auth"
import { autorun, observable } from "mobx"
import { observer } from "mobx-react"
import React from "react"
import ReactInputMask from "react-input-mask"
import { Redirect, useHistory, useRouteMatch } from "react-router-dom"
import { PATHS } from "Router"
import Store from "store/Store"
import { useRootClasses } from "style"

interface NewUserPageProps {}

enum Stage {
    CONFIRM_EMAIL,
    RESEND_INVITE,
    DETAILS,
    PHONE,
    NOT_ROBOT,
    VERIFY_CODE,
    LOGGED_IN,
}

class NewUserPageState {
    @observable
    componentName = "NewUserPage"

    @observable
    stage: Stage = Stage.CONFIRM_EMAIL

    @observable
    displayName = ""

    @observable
    email = ""

    @observable
    phone = ""

    @observable
    code = ""

    @observable
    reinvited?: boolean

    @observable
    title = ""

    @observable
    subtitle = ""
}

const NewUserPage: React.FC<NewUserPageProps> = (props) => {
    const history = useHistory()
    const rc = useRootClasses()
    const state = React.useRef(new NewUserPageState()).current
    const match = useRouteMatch()
    const { passwordChecker, invalidPassword, password } = usePasswordChecker([state.email, state.displayName])

    // if the link is without the firebase route, then render the option to resend invite.
    // Reference <FirebaseRoutes />
    React.useEffect(() => {
        if (match.path === PATHS.NEW_USER()) {
            state.stage = Stage.RESEND_INVITE
            state.title = "Resend Invite"
            state.subtitle = "Enter the email to resend an invite link to"
        }
    }, [match.path, state.stage, state.subtitle, state.title])

    React.useEffect(() => {
        let active = true
        const unsub = autorun(() => {
            if (Store.auth.authUser?.displayName && active) {
                state.displayName = Store.auth.authUser.displayName
            }
        })
        return () => {
            unsub()
            active = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const buttonAction = {
        [Stage.CONFIRM_EMAIL]: async () => {
            try {
                await Store.auth.signInWithEmailLink(state.email)
                state.stage = Stage.DETAILS
            } catch (e) {
                if (e.code === "auth/invalid-action-code") {
                    state.stage = Stage.RESEND_INVITE
                    // eslint-disable-next-line no-throw-literal
                    throw { message: "Hmmm, check that your email is correct and click the button below to resend invite!" }
                }
                throw e
            }
        },
        [Stage.RESEND_INVITE]: async () => {
            await firebase.auth().sendSignInLinkToEmail(state.email, {
                url: `${window.location.origin}${match.path}`,
                handleCodeInApp: true,
            })
            state.reinvited = true
        },
        [Stage.DETAILS]: async () => {
            await Store.auth.updatePassword(password)
            // displayName must be updated when twoFactor is true in State.VERIFY_CODE
            state.stage = Stage.PHONE
        },
        [Stage.PHONE]: async () => {
            state.stage = Stage.NOT_ROBOT
            await Store.auth.sendSMS(state.phone, "firebase-captcha", () => (state.stage = Stage.VERIFY_CODE), true)
        },
        [Stage.NOT_ROBOT]: async () => {},
        [Stage.VERIFY_CODE]: async () => {
            await Store.auth.verifyCodeAndLogin(state.code)
            // Can't update displayName until twoFactor is true
            await Store.auth.updateDisplayName(state.displayName)
            state.stage = Stage.LOGGED_IN
        },
        [Stage.LOGGED_IN]: async () => {},
    }[state.stage]

    const form = {
        [Stage.CONFIRM_EMAIL]: (
            <TextField
                label="Email"
                value={state.email}
                onChange={(e) => (state.email = e.target.value)}
                helperText="Use the email the invite was sent to"
                autoFocus
                autoComplete="username"
            />
        ),
        [Stage.RESEND_INVITE]: (
            <TextField
                label="Email"
                value={state.email}
                onChange={(e) => (state.email = e.target.value)}
                helperText="Use the email the invite was sent to"
                autoFocus
                autoComplete="username"
            />
        ),
        [Stage.DETAILS]: (
            <>
                {/* Email input duplicated and hidden so password managers can pick it up: https://www.chromium.org/developers/design-documents/form-styles-that-chromium-understands */}
                <TextField
                    label="Email"
                    value={state.email}
                    onChange={(e) => (state.email = e.target.value)}
                    helperText="Use the email the invite was sent to"
                    autoFocus
                    autoComplete="username"
                    style={{ display: "none" }}
                />
                <TextField label="Full Name" value={state.displayName} onChange={(e) => (state.displayName = e.target.value)} autoFocus autoComplete="name" />
                {passwordChecker}
            </>
        ),
        [Stage.PHONE]: (
            <ReactInputMask mask="(999) 999 9999" value={state.phone} onChange={(e) => (state.phone = e.target.value)}>
                {() => <TextField label="Phone" helperText="Required for 2-factor authentication" type="tel" autoFocus />}
            </ReactInputMask>
        ),
        [Stage.NOT_ROBOT]: (
            <>
                <ReactInputMask mask="(999) 999 9999" value={state.phone} onChange={(e) => (state.phone = e.target.value)}>
                    {() => <TextField label="Phone" helperText="Required for 2-factor authentication" type="tel" />}
                </ReactInputMask>
                <div id="firebase-captcha" className={clsx(rc.mt1, rc.mr1)} />
            </>
        ),
        [Stage.VERIFY_CODE]: (
            <>
                <ReactInputMask mask="(999) 999 9999" value={state.phone} onChange={(e) => (state.phone = e.target.value)} disabled>
                    {() => <TextField label="Phone" helperText="Required for 2-factor authentication" type="tel" />}
                </ReactInputMask>
                <TextField
                    label="6-digit Code"
                    value={state.code}
                    onChange={(e) => (state.code.length <= 6 ? (state.code = e.target.value) : state.code)}
                    autoFocus
                />
            </>
        ),
        [Stage.LOGGED_IN]: <Redirect to={PATHS.HOME()} />,
    }[state.stage]

    const buttons = {
        [Stage.CONFIRM_EMAIL]: (
            <>
                <PortalSubmitButton>Confirm</PortalSubmitButton>
            </>
        ),
        [Stage.RESEND_INVITE]: (
            <>
                {state.reinvited ? (
                    <Typography variant="h6">Please check your email for a fresh invite link</Typography>
                ) : (
                    <PortalSubmitButton>Resend Invite</PortalSubmitButton>
                )}
            </>
        ),
        [Stage.DETAILS]: <PortalSubmitButton disabled={invalidPassword}>Continue</PortalSubmitButton>,
        [Stage.PHONE]: (
            <>
                <PortalSubmitButton>Send Code</PortalSubmitButton>
                <Button onClick={() => (state.stage = Stage.DETAILS)} variant="outlined" fullWidth>
                    Back
                </Button>
            </>
        ),
        [Stage.NOT_ROBOT]: (
            <>
                <PortalSubmitButton>Resend Code</PortalSubmitButton>
                <Button onClick={() => (state.stage = Stage.PHONE)} variant="outlined" fullWidth>
                    Back
                </Button>
            </>
        ),

        [Stage.VERIFY_CODE]: (
            <>
                <PortalSubmitButton disabled={state.code.length !== 6}>Confirm</PortalSubmitButton>
                <Button onClick={() => (state.stage = Stage.NOT_ROBOT)} variant="outlined" fullWidth>
                    Back
                </Button>
            </>
        ),
        [Stage.LOGGED_IN]: (
            <PortalSubmitButton type="button" onClick={() => history.push(PATHS.HOME())}>
                Enter
            </PortalSubmitButton>
        ),
    }[state.stage]

    return (
        <PortalForm
            title={state.title || "Create Account"}
            subtitle={state.subtitle || "Welcome to Qlarity, a mindfulness intervention application for youth."}
            form={form}
            buttons={buttons}
            buttonAction={buttonAction}
        />
    )
}

export default observer(NewUserPage)
